import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Main component for the Bible view.
 * Handles testament selection, book/ chapter navigation and links to related pages.
 */
export default function Bible() {
  const [selectedBook, setSelectedBook] = useState('');
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [testament, setTestament] = useState<'old' | 'new'>('old');
  const navigate = useNavigate();

  // Livros do Antigo Testamento com número de capítulos
  const oldTestamentBooks = [
    { name: 'Gênesis', chapters: 50 },
    { name: 'Êxodo', chapters: 40 },
    { name: 'Levítico', chapters: 27 },
    { name: 'Números', chapters: 36 },
    { name: 'Deuteronômio', chapters: 34 },
    { name: 'Josué', chapters: 24 },
    { name: 'Juízes', chapters: 21 },
    { name: 'Rute', chapters: 4 },
    { name: '1 Samuel', chapters: 31 },
    { name: '2 Samuel', chapters: 24 },
    { name: '1 Reis', chapters: 22 },
    { name: '2 Reis', chapters: 25 },
    { name: '1 Crônicas', chapters: 29 },
    { name: '2 Crônicas', chapters: 36 },
    { name: 'Esdras', chapters: 10 },
    { name: 'Neemias', chapters: 13 },
    { name: 'Ester', chapters: 10 },
    { name: 'Jó', chapters: 42 },
    { name: 'Salmos', chapters: 150 },
    { name: 'Provérbios', chapters: 31 },
    { name: 'Eclesiastes', chapters: 12 },
    { name: 'Cântico dos Cânticos', chapters: 8 },
    { name: 'Isaías', chapters: 66 },
    { name: 'Jeremias', chapters: 52 },
    { name: 'Lamentações', chapters: 5 },
    { name: 'Ezequiel', chapters: 48 },
    { name: 'Daniel', chapters: 12 },
    { name: 'Oséias', chapters: 14 },
    { name: 'Joel', chapters: 3 },
    { name: 'Amós', chapters: 9 },
    { name: 'Obadias', chapters: 1 },
    { name: 'Jonas', chapters: 4 },
    { name: 'Miquéias', chapters: 7 },
    { name: 'Naum', chapters: 3 },
    { name: 'Habacuque', chapters: 3 },
    { name: 'Sofonias', chapters: 3 },
    { name: 'Ageu', chapters: 2 },
    { name: 'Zacarias', chapters: 14 },
    { name: 'Malaquias', chapters: 4 },
  ];

  // Livros do Novo Testamento com número de capítulos
  const newTestamentBooks = [
    { name: 'Mateus', chapters: 28 },
    { name: 'Marcos', chapters: 16 },
    { name: 'Lucas', chapters: 24 },
    { name: 'João', chapters: 21 },
    { name: 'Atos', chapters: 28 },
    { name: 'Romanos', chapters: 16 },
    { name: '1 Coríntios', chapters: 16 },
    { name: '2 Coríntios', chapters: 13 },
    { name: 'Gálatas', chapters: 6 },
    { name: 'Efésios', chapters: 6 },
    { name: 'Filipenses', chapters: 4 },
    { name: 'Colossenses', chapters: 4 },
    { name: '1 Tessalonicenses', chapters: 5 },
    { name: '2 Tessalonicenses', chapters: 3 },
    { name: '1 Timóteo', chapters: 6 },
    { name: '2 Timóteo', chapters: 4 },
    { name: 'Tito', chapters: 3 },
    { name: 'Filemom', chapters: 1 },
    { name: 'Hebreus', chapters: 13 },
    { name: 'Tiago', chapters: 5 },
    { name: '1 Pedro', chapters: 5 },
    { name: '2 Pedro', chapters: 3 },
    { name: '1 João', chapters: 5 },
    { name: '2 João', chapters: 1 },
    { name: '3 João', chapters: 1 },
    { name: 'Judas', chapters: 1 },
    { name: 'Apocalipse', chapters: 22 },
  ];

  const currentBooks = testament === 'old' ? oldTestamentBooks : newTestamentBooks;
  const selectedBookData = currentBooks.find(book => book.name === selectedBook);
  const maxChapters = selectedBookData ? selectedBookData.chapters : 50;

  /**
   * Safely navigates to a route, catching possible errors (e.g., bad paths)
   */
  const safeNavigate = (path: string) => {
    try {
      navigate(path);
    } catch (error) {
      console.error(`Navigation error to "${path}":`, error);
    }
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Error clearing user data from localStorage:', error);
    }
    safeNavigate('/login');
  };

  const handleViewStudy = (book: string, chapter: number, verse: number) => {
    const query = `book=${encodeURIComponent(book)}&chapter=${chapter}&verse=${verse}`;
    safeNavigate(`/studies?${query}`);
  };

  const handleViewCharacter = (book: string) => {
    safeNavigate('/characters');
  };

  // Função para obter versículos completos da Bíblia - TODOS OS LIVROS DO ANTIGO TESTAMENTO
  const getBibleVerse = (book: string, chapter: number, verse: number): string => {
    const bibleVerses: { [key: string]: { [key: number]: { [key: number]: string } } } = {
      'Gênesis': {
        1: {
          1: 'No princípio, Deus criou os céus e a terra.',
          2: 'E a terra era sem forma e vazia; e havia trevas sobre a face do abismo; e o Espírito de Deus se movia sobre a face das águas.',
          3: 'E disse Deus: Haja luz. E houve luz.',
          4: 'E viu Deus que era boa a luz; e fez Deus separação entre a luz e as trevas.',
          5: 'E Deus chamou à luz Dia; e às trevas chamou Noite. E foi a tarde e a manhã: o dia primeiro.',
          6: 'E disse Deus: Haja uma expansão no meio das águas, e haja separação entre águas e águas.',
          7: 'E fez Deus a expansão e fez separação entre as águas que estavam debaixo da expansão e as águas que estavam sobre a expansão. E assim foi.',
          8: 'E chamou Deus à expansão Céus; e foi a tarde e a manhã: o dia segundo.',
          9: 'E disse Deus: Ajuntem-se as águas debaixo dos céus num lugar; e apareça a porção seca. E assim foi.',
          10: 'E chamou Deus à porção seca Terra; e ao ajuntamento das águas chamou Mares. E viu Deus que era bom.',
          11: 'E disse Deus: Produza a terra erva verde, erva que dê semente, árvore frutífera que dê fruto segundo a sua espécie, cuja semente está nela conforme a sua espécie. E assim foi.',
          12: 'E a terra produziu erva, erva dando semente conforme a sua espécie e árvore frutífera, cuja semente está nela conforme a sua espécie. E viu Deus que era bom.',
          13: 'E foi a tarde e a manhã: o dia terceiro.',
          14: 'E disse Deus: Haja luminares na expansão dos céus, para haver separação entre o dia e a noite; e sejam eles para sinais e para tempos determinados e para dias e anos.',
          15: 'E sejam para luminares na expansão dos céus, para alumiar a terra. E assim foi.',
          16: 'E fez Deus os dois grandes luminares: o luminar maior para governar o dia, e o luminar menor para governar a noite; e fez as estrelas.',
          17: 'E Deus os pôs na expansão dos céus para alumiar a terra,',
          18: 'E para governar o dia e a noite, e para fazer separação entre a luz e as trevas. E viu Deus que era bom.',
          19: 'E foi a tarde e a manhã: o dia quarto.',
          20: 'E disse Deus: Produzam as águas abundantemente répteis de alma vivente; e voem as aves sobre a face da expansão dos céus.',
          21: 'E Deus criou as grandes baleias, e todo réptil de alma vivente que as águas abundantemente produziram conforme as suas espécies, e toda ave de asas conforme a sua espécie. E viu Deus que era bom.',
          22: 'E Deus os abençoou, dizendo: Frutificai, e multiplicai-vos, e enchei as águas nos mares; e as aves se multipliquem na terra.',
          23: 'E foi a tarde e a manhã: o dia quinto.',
          24: 'E disse Deus: Produza a terra alma vivente conforme a sua espécie; gado, e répteis, e bestas-feras da terra conforme a sua espécie. E assim foi.',
          25: 'E fez Deus as bestas-feras da terra conforme a sua espécie, e o gado conforme a sua espécie, e todo réptil da terra conforme a sua espécie. E viu Deus que era bom.',
          26: 'E disse Deus: Façamos o homem à nossa imagem, conforme a nossa semelhança; e domine sobre os peixes do mar, e sobre as aves dos céus, e sobre o gado, e sobre toda a terra, e sobre todo réptil que se move sobre a terra.',
          27: 'E criou Deus o homem à sua imagem; à imagem de Deus o criou; macho e fêmea os criou.',
          28: 'E Deus os abençoou e Deus lhes disse: Frutificai, e multiplicai-vos, e enchei a terra, e sujeitai-a; e dominai sobre os peixes do mar, e sobre as aves dos céus, e sobre todo o animal que se move sobre a terra.',
          29: 'E disse Deus: Eis que vos tenho dado toda erva que dá semente e que está sobre a face de toda a terra e toda árvore em que há fruto de árvore que dá semente; ser-vos-ão para mantimento.',
          30: 'E a todo animal da terra, e a toda ave dos céus, e a todo réptil da terra, em que há alma vivente, toda a erva verde lhes será para mantimento. E assim foi.',
          31: 'E viu Deus tudo quanto tinha feito, e eis que era muito bom; e foi a tarde e a manhã: o dia sexto.'
        },
        2: {
          1: 'Assim, os céus e a terra foram acabados, e todo o seu exército.',
          2: 'E havendo Deus acabado no dia sétimo a Sua obra, que tinha feito, descansou no sétimo dia de toda a Sua obra, que tinha feito.',
          3: 'E abençoou Deus o dia sétimo e o santificou, porque nele descansou de toda a Sua obra, que Deus criara e fizera.',
          4: 'Estas são as origens dos céus e da terra, quando foram criados; no dia em que o Senhor Deus fez a terra e os céus.',
          5: 'E toda planta do campo que ainda não estava na terra, e toda erva do campo que ainda não brotava; porque ainda o Senhor Deus não tinha feito chover sobre a terra, e não havia homem para lavrar a terra.',
          6: 'Um vapor, porém, subia da terra e regava toda a face da terra.',
          7: 'E formou o Senhor Deus o homem do pó da terra e soprou em seus narizes o fôlego da vida; e o homem foi feito alma vivente.',
          8: 'E plantou o Senhor Deus um jardim no Éden, da banda do Oriente; e pôs ali o homem que tinha formado.',
          9: 'E o Senhor Deus fez brotar da terra toda árvore agradável à vista e boa para comida; e a árvore da vida no meio do jardim, e a árvore da ciência do bem e do mal.',
          10: 'E saía um rio do Éden para regar o jardim; e dali se dividia e se tornava em quatro cabeças.',
          11: 'O nome do primeiro é Pisom; este é o que rodeia toda a terra de Havilá, onde há ouro.',
          12: 'E o ouro dessa terra é bom; ali há o bdélio e a pedra sardônica.',
          13: 'E o nome do segundo rio é Giom; este é o que rodeia toda a terra de Cuxe.',
          14: 'E o nome do terceiro rio é Hidéquel; este é o que vai para a banda do Oriente da Assíria; e o quarto rio é o Eufrates.',
          15: 'E tomou o Senhor Deus o homem e o pôs no jardim do Éden para o lavrar e o guardar.',
          16: 'E ordenou o Senhor Deus ao homem, dizendo: De toda árvore do jardim comerás livremente,',
          17: 'mas da árvore da ciência do bem e do mal, dela não comerás; porque, no dia em que dela comeres, certamente morrerás.',
          18: 'E disse o Senhor Deus: Não é bom que o homem esteja só; far-lhe-ei uma adjutora que esteja como diante dele.',
          19: 'Havendo, pois, o Senhor Deus formado da terra todo animal do campo e toda ave dos céus, os trouxe a Adão, para este ver como lhes chamaria; e tudo o que Adão chamou a toda a alma vivente, isso foi o seu nome.',
          20: 'E Adão pôs os nomes a todo o gado, e às aves dos céus, e a todo animal do campo; mas para o homem não se achava adjutora que estivesse como diante dele.',
          21: 'Então, o Senhor Deus fez cair um sono pesado sobre Adão, e este adormeceu; e tomou uma das suas costelas e cerrou a carne em seu lugar.',
          22: 'E da costela que o Senhor Deus tomou do homem formou uma mulher; e trouxe-a a Adão.',
          23: 'E disse Adão: Esta é agora osso dos meus ossos e carne da minha carne; esta será chamada varoa, porquanto do varão foi tomada.',
          24: 'Portanto, deixará o varão o seu pai e a sua mãe e apegar-se-á à sua mulher, e serão ambos uma carne.',
          25: 'E ambos estavam nus, o homem e a sua mulher; e não se envergonhavam.'
        },
        3: {
          1: 'Ora, a serpente era mais astuta que todas as alimárias do campo que o Senhor Deus tinha feito; e esta disse à mulher: É assim que Deus disse: Não comereis de toda árvore do jardim?',
          2: 'E disse a mulher à serpente: Do fruto das árvores do jardim comeremos,',
          3: 'mas do fruto da árvore que está no meio do jardim, disse Deus: Não comereis dele, nem nele tocareis, para que não morrais.',
          4: 'Então, a serpente disse à mulher: Certamente não morrereis.',
          5: 'Porque Deus sabe que, no dia em que dele comerdes, se abrirão os vossos olhos, e sereis como Deus, sabendo o bem e o mal.',
          6: 'E, vendo a mulher que aquela árvore era boa para se comer, e agradável aos olhos, e árvore desejável para dar entendimento, tomou do seu fruto, e comeu, e deu também a seu marido, que estava com ela, e ele comeu.',
          7: 'Então, foram abertos os olhos de ambos, e conheceram que estavam nus; e coseram folhas de figueira, e fizeram para si aventais.',
          8: 'E ouviram a voz do Senhor Deus, que passeava no jardim pela viração do dia; e esconderam-se Adão e sua mulher da presença do Senhor Deus, entre as árvores do jardim.',
          9: 'E chamou o Senhor Deus a Adão e disse-lhe: Onde estás?',
          10: 'E ele disse: Ouvi a tua voz soar no jardim, e temi, porque estava nu, e escondi-me.',
          11: 'E Deus disse: Quem te mostrou que estavas nu? Comeste tu da árvore de que te ordenei que não comesses?',
          12: 'Então, disse Adão: A mulher que me deste por companheira, ela me deu da árvore, e comi.',
          13: 'E disse o Senhor Deus à mulher: Por que fizeste isto? E disse a mulher: A serpente me enganou, e eu comi.',
          14: 'Então, o Senhor Deus disse à serpente: Porquanto fizeste isto, maldita serás mais que toda besta e mais que todos os animais do campo; sobre o teu ventre andarás e pó comerás todos os dias da tua vida.',
          15: 'E porei inimizade entre ti e a mulher e entre a tua semente e a sua semente; esta te ferirá a cabeça, e tu lhe ferirás o calcanhar.',
          16: 'E à mulher disse: Multiplicarei grandemente a tua dor e a tua conceição; com dor terás filhos; e o teu desejo será para o teu marido, e ele te dominará.',
          17: 'E a Adão disse: Porquanto deste ouvidos à voz de tua mulher e comeste da árvore de que te ordenei, dizendo: Não comerás dela, maldita é a terra por causa de ti; com dor comerás dela todos os dias da tua vida.',
          18: 'Espinhos e cardos também te produzirá; e comerás a erva do campo.',
          19: 'No suor do teu rosto, comerás o teu pão, até que te tornes à terra; porque dela foste tomado, porquanto és pó e em pó te tornarás.',
          20: 'E chamou Adão o nome de sua mulher Eva, porquanto era a mãe de todos os viventes.',
          21: 'E fez o Senhor Deus a Adão e à sua mulher túnicas de peles e os vestiu.',
          22: 'Então, disse o Senhor Deus: Eis que o homem é como um de nós, sabendo o bem e o mal; ora, pois, para que não estenda a sua mão, e tome também da árvore da vida, e coma, e viva eternamente,',
          23: 'o Senhor Deus o lançou fora do jardim do Éden, para lavrar a terra de que fora tomado.',
          24: 'E, havendo lançado fora o homem, pôs querubins ao oriente do jardim do Éden e uma espada inflamada que andava ao redor, para guardar o caminho da árvore da vida.'
        }
      },
      'Êxodo': {
        3: {
          1: 'E apascentava Moisés o rebanho de Jetro, seu sogro, sacerdote de Midiã; e levou o rebanho atrás do deserto e chegou ao monte de Deus, a Horebe.',
          2: 'E apareceu-lhe o Anjo do Senhor em uma chama de fogo, no meio de uma sarça; e olhou, e eis que a sarça ardia no fogo, e a sarça não se consumia.',
          3: 'E Moisés disse: Agora me virarei e verei esta grande visão, porque a sarça se não queima.',
          4: 'E, vendo o Senhor que se virava para ele, bradou Deus a ele do meio da sarça e disse: Moisés! Moisés! E ele disse: Eis-me aqui.',
          5: 'E disse: Não te chegues para cá; tira os teus sapatos de teus pés, porque o lugar em que tu estás é terra santa.',
          6: 'Disse mais: Eu sou o Deus de teu pai, o Deus de Abraão, o Deus de Isaque e o Deus de Jacó. E Moisés encobriu o seu rosto, porque temeu olhar para Deus.',
          7: 'E disse o Senhor: Tenho visto atentamente a aflição do meu povo, que está no Egito, e tenho ouvido o seu clamor por causa dos seus exatores, porque conheci as suas dores.',
          8: 'Portanto, desci para livrá-lo da mão dos egípcios e para fazê-lo subir daquela terra a uma terra boa e larga, terra que mana leite e mel, ao lugar do cananeu, e do heteu, e do amorreu, e do perizeu, e do heveu, e do jebuseu.',
          9: 'E, agora, eis que o clamor dos filhos de Israel chegou a mim, e também tenho visto a opressão com que os egípcios os oprimem.',
          10: 'Vem, agora, pois, e eu te enviarei a Faraó, para que tires o meu povo, os filhos de Israel, do Egito.',
          11: 'Então, Moisés disse a Deus: Quem sou eu, que vá a Faraó e tire do Egito os filhos de Israel?',
          12: 'E disse: Certamente eu serei contigo; e isto te será por sinal de que eu te enviei: quando houveres tirado este povo do Egito, servirest a Deus neste monte.',
          13: 'E disse Moisés a Deus: Eis que, quando eu for aos filhos de Israel e lhes disser: O Deus de vossos pais me enviou a vós outros; e eles me perguntarem: Qual é o seu nome? Que lhes direi?',
          14: 'E disse Deus a Moisés: EU SOU O QUE SOU. Disse mais: Assim dirás aos filhos de Israel: EU SOU me enviou a vós outros.',
          15: 'E Deus disse mais a Moisés: Assim dirás aos filhos de Israel: O Senhor, o Deus de vossos pais, o Deus de Abraão, o Deus de Isaque e o Deus de Jacó, me enviou a vós outros; este é meu nome eternamente, e este é meu memorial de geração em geração.'
        },
        20: {
          1: 'Então, falou Deus todas estas palavras, dizendo:',
          2: 'Eu sou o Senhor, teu Deus, que te tirei da terra do Egito, da casa da servidão.',
          3: 'Não terás outros deuses diante de mim.',
          4: 'Não farás para ti imagem de escultura, nem alguma semelhança do que há em cima nos céus, nem em baixo na terra, nem nas águas debaixo da terra.',
          5: 'Não te encurvarás a elas nem as servirás; porque eu, o Senhor, teu Deus, sou Deus zeloso, que visito a maldade dos pais nos filhos até à terceira e quarta geração daqueles que me aborrecem',
          6: 'e faço misericórdia em milhares aos que me amam e guardam os meus mandamentos.',
          7: 'Não tomarás o nome do Senhor, teu Deus, em vão, porque o Senhor não terá por inocente o que tomar o seu nome em vão.',
          8: 'Lembra-te do dia de sábado, para o santificar.',
          9: 'Seis dias trabalharás e farás toda a tua obra,',
          10: 'mas o sétimo dia é o sábado do Senhor, teu Deus; não farás nenhuma obra, nem tu, nem o teu filho, nem a tua filha, nem o teu servo, nem a tua serva, nem o teu animal, nem o teu estrangeiro que está dentro das tuas portas.',
          11: 'Porque em seis dias fez o Senhor os céus e a terra, o mar e tudo que neles há e ao sétimo dia descansou; portanto, abençoou o Senhor o dia de sábado e o santificou.',
          12: 'Honra a teu pai e a tua mãe, para que se prolonguem os teus dias na terra que o Senhor, teu Deus, te dá.',
          13: 'Não matarás.',
          14: 'Não adulterarás.',
          15: 'Não furtarás.',
          16: 'Não dirás falso testemunho contra o teu próximo.',
          17: 'Não cobiçarás a casa do teu próximo; não cobiçarás a mulher do teu próximo, nem o seu servo, nem a sua serva, nem o seu boi, nem o seu jumento, nem coisa alguma do teu próximo.'
        }
      },
      'Levítico': {
        19: {
          1: 'Falou mais o Senhor a Moisés, dizendo:',
          2: 'Fala a toda a congregação dos filhos de Israel e dize-lhes: Santos sereis, porque eu, o Senhor, vosso Deus, sou santo.',
          3: 'Cada um temerá a sua mãe e a seu pai e guardará os meus sábados. Eu sou o Senhor, vosso Deus.',
          4: 'Não vos virareis para os ídolos, nem vos fareis deuses de fundição. Eu sou o Senhor, vosso Deus.',
          9: 'E, quando segardes a sega da vossa terra, não segarás totalmente os cantos do teu campo, nem colherás as espigas caídas da tua sega.',
          10: 'Semelhantemente não rabiscarás a tua vinha, nem colherás os bagos caídos da tua vinha; deixá-los-ás ao pobre e ao estrangeiro. Eu sou o Senhor, vosso Deus.',
          11: 'Não furtareis, nem mentireis, nem usareis de falsidade cada um com o seu próximo.',
          12: 'E não jurareis falso pelo meu nome, pois profanarás o nome do teu Deus. Eu sou o Senhor.',
          13: 'Não oprimirás o teu próximo, nem o roubarás; a paga do diarista não ficará contigo até pela manhã.',
          14: 'Não amaldiçoarás ao surdo, nem porás tropeço diante do cego; mas temerás o teu Deus. Eu sou o Senhor.',
          15: 'Não fareis injustiça no juízo; não respeitarás o pobre, nem honrarás o poderoso; com justiça julgarás o teu próximo.',
          16: 'Não andarás como mexeriqueiro entre o teu povo; não te porás contra o sangue do teu próximo. Eu sou o Senhor.',
          17: 'Não aborrecerás a teu irmão no teu coração; não deixarás de repreender o teu próximo e não levarás sobre ti pecado por causa dele.',
          18: 'Não te vingarás, nem guardarás ira contra os filhos do teu povo; mas amarás o teu próximo como a ti mesmo. Eu sou o Senhor.',
          19: 'Guardareis os meus estatutos; não permitirás que se ajuntem misturadamente os teus animais de diferentes espécies; no teu campo, não semearás semente de mistura, e veste de diversos estofos misturados não vestirás.',
          20: 'E, quando um homem se deitar com mulher que for serva desposada com outro homem, e não for resgatada, nem se lhe houver dado liberdade, então, serão açoitados; não morrerão, pois não era livre.'
        }
      },
      'Números': {
        6: {
          24: 'O Senhor te abençoe e te guarde;',
          25: 'o Senhor faça resplandecer o seu rosto sobre ti e tenha misericórdia de ti;',
          26: 'o Senhor sobre ti levante o seu rosto e te dê a paz.'
        }
      },
      'Deuteronômio': {
        6: {
          4: 'Ouve, Israel, o Senhor, nosso Deus, é o único Senhor.',
          5: 'Amarás, pois, o Senhor, teu Deus, de todo o teu coração, e de toda a tua alma, e de todo o teu poder.',
          6: 'E estas palavras que hoje te ordeno estarão no teu coração;',
          7: 'e as intimarás a teus filhos e delas falarás assentado em tua casa, e andando pelo caminho, e deitando-te, e levantando-te.',
          8: 'Também as atarás por sinal na tua mão, e te serão por testeiras entre os teus olhos.',
          9: 'E as escreverás nos umbrais de tua casa e nas tuas portas.'
        }
      },
      'Josué': {
        1: {
          8: 'Não se aparte da tua boca o livro desta Lei; antes, medita nele de dia e de noite, para que tenhas cuidado de fazer conforme tudo quanto nele está escrito; porque, então, farás prosperar o teu caminho e, então, prudentemente te conduzirás.',
          9: 'Não to mandei eu? Esforça-te e tem bom ânimo; não pasmes, nem te espantes, porque o Senhor, teu Deus, é contigo, por onde quer que andares.'
        }
      },
      'Juízes': {
        6: {
          12: 'Então, o Anjo do Senhor lhe apareceu e lhe disse: O Senhor é contigo, varão valoroso.',
          13: 'Mas Gideão lhe disse: Ai, Senhor meu, se o Senhor é conosco, por que tudo isto nos sobreveio? E onde estão todas as suas maravilhas que nossos pais nos contaram, dizendo: Não nos fez o Senhor subir do Egito? Porém, agora, o Senhor nos desamparou e nos entregou nas mãos dos midianitas.',
          14: 'Então, se virou o Senhor para ele e disse: Vai nesta tua força e livrarás a Israel das mãos dos midianitas; porventura, não te enviei eu?',
          15: 'E ele lhe disse: Ai, Senhor meu, com que livrarei a Israel? Eis que a minha família é a mais pobre em Manassés, e eu, o menor na casa de meu pai.',
          16: 'E o Senhor lhe disse: Porque eu hei de ser contigo, tu ferirás aos midianitas como se fossem um só homem.'
        }
      },
      'Rute': {
        1: {
          16: 'Disse, porém, Rute: Não me instes para que te deixe e me afaste de ti; porque, aonde quer que tu fores, irei eu e, onde quer que pousares, ali pousarei eu; o teu povo é o meu povo, o teu Deus é o meu Deus.',
          17: 'Onde quer que morreres, morrere eu e ali serei sepultada; faça-me assim o Senhor e outro tanto, se outra coisa que não seja a morte me separar de ti.'
        }
      },
      '1 Samuel': {
        3: {
          9: 'Pelo que Eli disse a Samuel: Vai-te deitar, e há de ser que, se te chamar, dirás: Fala, Senhor, porque o teu servo ouve. Então, Samuel se foi e se deitou no seu lugar.',
          10: 'Então, veio o Senhor, e pôs-se ali, e chamou como das outras vezes: Samuel, Samuel! E disse Samuel: Fala, porque o teu servo ouve.'
        },
        17: {
          45: 'Davi, porém, disse ao filisteu: Tu vens a mim com espada, e com lança, e com escudo; porém eu venho a ti em nome do Senhor dos Exércitos, a quem tens afrontado.',
          46: 'Hoje mesmo, o Senhor te entregará na minha mão, e ferir-te-ei, e tirar-te-ei a cabeça, e os corpos do arraial dos filisteus darei hoje mesmo às aves dos céus e às feras da terra; e toda a terra saberá que há Deus em Israel.',
          47: 'E saberá toda esta congregação que o Senhor salva, não com espada, nem com lança; porque do Senhor é a guerra, e ele vos entregará na nossa mão.'
        }
      },
      '2 Samuel': {
        7: {
          12: 'Quando teus dias forem completados, e vieres a dormir com teus pais, então, farei levantar depois de ti a tua semente, que sair das tuas entranhas, e confirmarei o seu reino.',
          13: 'Este edificará uma casa ao meu nome, e confirmarei o trono do seu reino para sempre.',
          14: 'Eu lhe serei por pai, e ele me será por filho; e, se vier a transgredir, castigá-lo-ei com vara de homens e com açoites de filhos de homens.',
          15: 'Mas a minha benignidade se não apartará dele, como a tirei de Saul, a quem tirei de diante de ti.',
          16: 'Porém a tua casa e o teu reino serão firmados para sempre diante de ti; teu trono será firme para sempre.'
        }
      },
      '1 Reis': {
        3: {
          9: 'Dá, pois, ao teu servo um coração entendido para julgar a teu povo, para que prudentemente discirna entre o bem e o mal; porque quem poderia julgar a este teu tão grande povo?',
          10: 'E pareceu esta palavra bem aos olhos do Senhor, que Salomão pedisse esta coisa.',
          11: 'E disse-lhe Deus: Porquanto pediste esta coisa e não pediste para ti muitos dias, nem pediste para ti riquezas, nem pediste a vida de teus inimigos, mas pediste para ti entendimento para discernir o juízo,',
          12: 'eis que fiz segundo as tuas palavras; eis que te dei um coração tão sábio e entendido, que antes de ti teu igual não houve, e depois de ti teu igual se não levantará.'
        }
      },
      '2 Reis': {
        2: {
          11: 'E sucedeu que, indo eles andando e falando, eis que um carro de fogo, com cavalos de fogo, os separou um do outro; e Elias subiu ao céu num redemoinho.',
          12: 'O que vendo Eliseu, clamou: Meu pai, meu pai, carros de Israel e seus cavaleiros! E nunca mais o viu; e, tomando as suas vestes, as rasgou em duas partes.'
        }
      },
      '1 Crônicas': {
        16: {
          8: 'Louvai ao Senhor, invocai o seu nome, fazei conhecidos os seus feitos entre os povos.',
          9: 'Cantai-lhe, cantai-lhe salmos, falai de todas as suas maravilhas.',
          10: 'Gloriai-vos no seu santo nome; alegre-se o coração daqueles que buscam ao Senhor.',
          11: 'Buscai ao Senhor e a sua força; buscai a sua face continuamente.'
        }
      },
      '2 Crônicas': {
        7: {
          14: 'E se o meu povo, que se chama pelo meu nome, se humilhar, e orar, e buscar a minha face, e se converter dos seus maus caminhos, então, eu ouvirei dos céus, e perdoarei os seus pecados, e sararei a sua terra.'
        }
      },
      'Esdras': {
        3: {
          11: 'E cantavam, louvando e dando graças ao Senhor: Porque ele é bom, porque a sua benignidade dura para sempre sobre Israel. E todo o povo jubilou com grande júbilo, quando louvaram ao Senhor, pela fundação da Casa do Senhor.'
        }
      },
      'Neemias': {
        8: {
          10: 'Disse-lhes mais: Ide, comei as gorduras, e bebei as doçuras, e enviai porções aos que não têm nada preparado para si; porque este dia é consagrado ao nosso Senhor; portanto, não vos entristeçais, porque a alegria do Senhor é a vossa força.'
        }
      },
      'Ester': {
        4: {
          14: 'Porque, se de todo te calares neste tempo, socorro e livramento de outra parte sairá para os judeus, mas tu e a casa de teu pai perecereis; e quem sabe se para tal tempo como este chegaste a este reino?'
        }
      },
      'Jó': {
        19: {
          25: 'Porque eu sei que o meu Redentor vive e que por fim se levantará sobre a terra.',
          26: 'E, depois de consumida a minha pele, ainda na minha carne verei a Deus.',
          27: 'Vê-lo-ei, por mim mesmo, e os meus olhos, e não outros, o contemplarão; e por isso os meus rins se consomem no meu interior.'
        }
      },
      'Salmos': {
        1: {
          1: 'Bem-aventurado o varão que não anda no conselho dos ímpios, nem se detém no caminho dos pecadores, nem se assenta na roda dos escarnecedores.',
          2: 'Antes, tem o seu prazer na lei do Senhor, e na sua lei medita de dia e de noite.',
          3: 'Pois será como a árvore plantada junto a ribeiros de águas, a qual dá o seu fruto na estação própria, e cujas folhas não caem; e tudo quanto fizer prosperará.',
          4: 'Não são assim os ímpios; mas são como a moinha que o vento espalha.',
          5: 'Por isso, os ímpios não subsistirão no juízo, nem os pecadores na congregação dos justos.',
          6: 'Porque o Senhor conhece o caminho dos justos, mas o caminho dos ímpios perecerá.'
        },
        23: {
          1: 'O Senhor é o meu pastor; nada me faltará.',
          2: 'Deitar-me faz em verdes pastos, guia-me mansamente a águas tranquilas.',
          3: 'Refrigera a minha alma; guia-me pelas veredas da justiça por amor do seu nome.',
          4: 'Ainda que eu andasse pelo vale da sombra da morte, não temeria mal algum, porque tu estás comigo; a tua vara e o teu cajado me consolam.',
          5: 'Preparas uma mesa perante mim na presença dos meus inimigos, unges a minha cabeça com óleo, o meu cálice transborda.',
          6: 'Certamente que a bondade e a misericórdia me seguirão todos os dias da minha vida; e habitarei na Casa do Senhor por longos dias.'
        },
        91: {
          1: 'Aquele que habita no esconderijo do Altíssimo, à sombra do Onipotente descansará.',
          2: 'Direi do Senhor: Ele é o meu Deus, o meu refúgio, a minha fortaleza, e nele confiarei.',
          3: 'Porque ele te livrará do laço do passarinheiro e da peste perniciosa.',
          4: 'Ele te cobrirá com as suas penas, e debaixo das suas asas te confiarás; a sua verdade será o teu escudo e broquel.',
          5: 'Não temerás o terror de noite nem a seta que voa de dia,',
          6: 'nem a peste que anda na escuridão, nem a mortandade que assola ao meio-dia.',
          7: 'Mil cairão ao teu lado, e dez mil, à tua direita, mas tu não serás atingido.',
          8: 'Somente com os teus olhos contemplarás e verás a recompensa dos ímpios.',
          9: 'Porque tu, ó Senhor, és o meu refúgio. No Altíssimo fizeste a tua habitação.',
          10: 'Nenhum mal te sucederá, nem praga alguma chegará à tua tenda.',
          11: 'Porque aos seus anjos dará ordem a teu respeito, para te guardarem em todos os teus caminhos.',
          12: 'Eles te sustentarão nas suas mãos, para que não tropeces com o teu pé em pedra.',
          13: 'Pisarás o leão e a cobra; calcarás aos pés o filho do leão e a serpente.',
          14: 'Porquanto tão encarecidamente me amou, também eu o livrarei; pô-lo-ei em retiro alto, porque conheceu o meu nome.',
          15: 'Ele me invocará, e eu lhe responderei; estarei com ele na angústia; dela o retirarei e o glorificarei.',
          16: 'Fartá-lo-ei com longura de dias e lhe mostrarei a minha salvação.'
        },
        119: {
          1: 'Bem-aventurados os retos em seus caminhos, que andam na lei do Senhor.',
          2: 'Bem-aventurados os que guardam os seus testemunhos e o buscam de todo o coração.',
          3: 'E não praticam iniquidade, mas andam em seus caminhos.',
          4: 'Tu ordenaste os teus mandamentos, para que diligentemente os observássemos.',
          5: 'Tomara que os meus caminhos fossem dirigidos a observar os teus estatutos!',
          6: 'Então, não ficaria confundido, atentando eu para todos os teus mandamentos.',
          7: 'Louvar-te-ei com retidão de coração, quando tiver aprendido os juízos da tua justiça.',
          8: 'Observarei os teus estatutos; não me desampares totalmente.',
          9: 'Como purificará o jovem o seu caminho? Observando-o conforme a tua palavra.',
          10: 'De todo o meu coração te busquei; não me deixes desviar dos teus mandamentos.',
          11: 'Escondi a tua palavra no meu coração, para eu não pecar contra ti.',
          12: 'Bendito és tu, ó Senhor! Ensina-me os teus estatutos.',
          13: 'Com os meus lábios declarei todos os juízos da tua boca.',
          14: 'Folguei tanto no caminho dos teus testemunhos como em todas as riquezas.',
          15: 'Meditarei nos teus preceitos e olharei para os teus caminhos.',
          16: 'Recrear-me-ei nos teus estatutos; não me esquecerei da tua palavra.'
        }
      },
      'Provérbios': {
        3: {
          5: 'Confia no Senhor de todo o teu coração e não te estribes no teu próprio entendimento.',
          6: 'Reconhece-o em todos os teus caminhos, e ele endireitará as tuas veredas.',
          7: 'Não sejas sábio a teus próprios olhos; teme ao Senhor e aparta-te do mal.',
          8: 'Isto será saúde para o teu umbigo e medula para os teus ossos.'
        },
        31: {
          10: 'Mulher virtuosa, quem a achará? O seu valor muito excede o de rubis.',
          11: 'O coração do seu marido está nela confiado, e a ela nenhuma fazenda faltará.',
          12: 'Ela lhe faz bem e não mal, todos os dias da sua vida.',
          13: 'Busca lã e linho e trabalha de boa vontade com as suas mãos.',
          14: 'É como os navios mercantes; de longe traz o seu pão.',
          15: 'Levanta-se, ainda sendo noite, e dá mantimento à sua casa e a tarefa às suas servas.',
          16: 'Examina uma herdade e adquire-a; planta uma vinha com o fruto das suas mãos.',
          17: 'Cinge os seus lombos de força e corrobora os seus braços.',
          18: 'Vê que é boa a sua mercancia; a sua lâmpada não se apaga de noite.',
          19: 'Estende as suas mãos ao fuso, e as suas mãos pegam na roca.',
          20: 'Abre a sua mão ao aflito; e ao necessitado estende as suas mãos.',
          21: 'Não temerás a neve por sua casa, porque toda a sua casa anda vestida de roupa dobrada.',
          22: 'Faz para si tapeçaria; de linho fino e púrpura é o seu vestido.',
          23: 'Conhece-se o seu marido nas portas, quando se assenta com os anciãos da terra.',
          24: 'Faz panos de linho fino, e vende-os, e dá cintas aos mercadores.',
          25: 'A força e a honra são o seu vestido; e se alegra com o dia futuro.',
          26: 'Abre a sua boca com sabedoria; e a lei da beneficência está na sua língua.',
          27: 'Atenta pelos caminhos da sua casa, e não come o pão da preguiça.',
          28: 'Levantam-se seus filhos e chamam-la bem-aventurada; como também seu marido, que a louva, dizendo:',
          29: 'Muitas filhas têm procedido virtuosamente, mas tu a todas sobrepujas.',
          30: 'Enganosa é a graça, e vã, a formosura; mas a mulher que teme ao Senhor, essa será louvada.',
          31: 'Dai-lhe do fruto das suas mãos, e louvem-na nas portas as suas obras.'
        }
      },
      'Eclesiastes': {
        3: {
          1: 'Tudo tem o seu tempo determinado, e há tempo para todo o propósito debaixo do céu:',
          2: 'tempo de nascer e tempo de morrer; tempo de plantar e tempo de arrancar o que se plantou;',
          3: 'tempo de matar e tempo de curar; tempo de derrubar e tempo de edificar;',
          4: 'tempo de chorar e tempo de rir; tempo de prantear e tempo de saltar;',
          5: 'tempo de espalhar pedras e tempo de ajuntar pedras; tempo de abraçar e tempo de afastar-se de abraçar;',
          6: 'tempo de buscar e tempo de perder; tempo de guardar e tempo de deitar fora;',
          7: 'tempo de rasgar e tempo de coser; tempo de estar calado e tempo de falar;',
          8: 'tempo de amar e tempo de aborrecer; tempo de guerra e tempo de paz.'
        }
      },
      'Cântico dos Cânticos': {
        2: {
          10: 'Fala o meu amado e me diz: Levanta-te, querida minha, formosa minha, e vem.',
          11: 'Porque eis que passou o inverno; a chuva cessou e se foi.',
          12: 'Aparecem as flores na terra, o tempo de cantar chega, e a voz da rola ouve-se em nossa terra.',
          13: 'A figueira já deu os seus figos verdes, e as vides em flor exalam o seu aroma; levanta-te, querida minha, formosa minha, e vem.'
        }
      },
      'Isaías': {
        53: {
          1: 'Quem deu crédito à nossa pregação? E a quem se manifestou o braço do Senhor?',
          2: 'Porque foi subindo como renovo perante ele e como raiz de uma terra seca; não tinha aparência nem formosura; e, olhando nós para ele, nenhuma beleza víamos, para que o desejássemos.',
          3: 'Era desprezado e o mais indigno entre os homens, homem de dores, experimentado nos trabalhos; e, como um de quem os homens escondiam o rosto, era desprezado, e não fizemos dele caso algum.',
          4: 'Verdadeiramente, ele tomou sobre si as nossas enfermidades e as nossas dores levou sobre si; e nós o reputávamos por aflito, ferido de Deus e oprimido.',
          5: 'Mas ele foi ferido pelas nossas transgressões e moído pelas nossas iniquidades; o castigo que nos traz a paz estava sobre ele, e, pelas suas pisaduras, fomos sarados.',
          6: 'Todos nós andávamos desgarrados como ovelhas; cada um se desviava pelo seu caminho, mas o Senhor fez cair sobre ele a iniquidade de nós todos.',
          7: 'Ele foi oprimido, mas não abriu a boca; como um cordeiro, foi levado ao matadouro e, como a ovelha muda perante os seus tosquiadores, ele não abriu a boca.',
          8: 'Da opressão e do juízo foi tirado; e quem contará o tempo da sua vida? Porquanto foi cortado da terra dos viventes; pela transgressão do meu povo, ele foi atingido.',
          9: 'E puseram a sua sepultura com os ímpios e com o rico, na sua morte; porquanto nunca fez injustiça, nem houve engano na sua boca.',
          10: 'Todavia, ao Senhor agradou moê-lo, fazendo-o enfermar; quando a sua alma se puser por expiação do pecado, verá a sua posteridade, prolongará os dias, e o bom prazer do Senhor prosperará na sua mão.',
          11: 'O trabalho da sua alma ele verá e ficará satisfeito; com o seu conhecimento, o meu servo, o justo, justificará a muitos, porque as iniquidades deles levará sobre si.',
          12: 'Pelo que lhe darei a parte de muitos, e, com os poderosos, repartirá ele o despojo, porquanto derramou a sua alma na morte e foi contado com os transgressores; mas ele levou sobre si o pecado de muitos e pelos transgressores intercedeu.'
        }
      },
      'Jeremias': {
        29: {
          11: 'Porque eu bem sei os pensamentos que tenho a vosso respeito, diz o Senhor; pensamentos de paz e não de mal, para vos dar o fim que esperais.',
          12: 'Então, me invocareis, e ireis, e orareis a mim, e eu vos ouvirei.',
          13: 'E buscar-me-eis e me achareis quando me buscardes de todo o vosso coração.'
        }
      },
      'Lamentações': {
        3: {
          22: 'As misericórdias do Senhor são a causa de não sermos consumidos, porque as suas misericórdias não têm fim.',
          23: 'Novas são cada manhã; grande é a tua fidelidade.',
          24: 'A minha porção é o Senhor, diz a minha alma; portanto, esperarei nele.'
        }
      },
      'Ezequiel': {
        36: {
          26: 'E dar-vos-ei um coração novo e porei dentro de vós um espírito novo; e tirarei da vossa carne o coração de pedra e vos darei um coração de carne.',
          27: 'E porei dentro de vós o meu Espírito e farei que andeis nos meus estatutos, e guardeis os meus juízos, e os observeis.'
        }
      },
      'Daniel': {
        3: {
          17: 'Eis que o nosso Deus, a quem nós servimos, é que nos pode livrar; ele nos livrará da fornalha de fogo ardente e da tua mão, ó rei.',
          18: 'E, se não, fica sabendo, ó rei, que não serviremos a teus deuses nem adoraremos a estátua de ouro que levantaste.'
        }
      },
      'Oséias': {
        6: {
          1: 'Vinde, e tornemos para o Senhor, porque ele despedaçou, e nos sarará; fez a ferida, e a ligará.',
          2: 'Depois de dois dias, nos dará a vida; ao terceiro dia, nos ressuscitará, e viveremos diante dele.',
          3: 'Então, conheceremos e prosseguiremos em conhecer ao Senhor; a sua saída, como a alva, é certa; e ele a nós virá como a chuva, como chuva serôdia que rega a terra.'
        }
      },
      'Joel': {
        2: {
          28: 'E há de ser que, depois, derramarei o meu Espírito sobre toda a carne, e vossos filhos e vossas filhas profetizarão, os vossos velhos terão sonhos, os vossas jovens terão visões.',
          29: 'E também sobre os servos e sobre as servas, naqueles dias, derramarei o meu Espírito.'
        }
      },
      'Amós': {
        5: {
          24: 'Corra, porém, o juízo como as águas, e a justiça, como o ribeiro impetuoso.'
        }
      },
      'Obadias': {
        1: {
          15: 'Porque o Dia do Senhor está perto sobre todas as nações; como tu fizeste, assim se fará contigo; a tua recompensa tornará sobre a tua cabeça.'
        }
      },
      'Jonas': {
        2: {
          8: 'Os que observam as vaidades vãs deixam a sua misericórdia.',
          9: 'Mas eu, com a voz do agradecimento, te oferecerei sacrifício; o que votei pagarei. Do Senhor vem a salvação.'
        }
      },
      'Miquéias': {
        6: {
          8: 'Ele te declarou, ó homem, o que é bom; e que é o que o Senhor pede de ti, senão que pratiques a justiça, e ames a beneficência, e andes humildemente com o teu Deus?'
        }
      },
      'Naum': {
        1: {
          7: 'O Senhor é bom, uma fortaleza no dia da angústia, e conhece os que confiam nele.'
        }
      },
      'Habacuque': {
        2: {
          4: 'Eis que de orgulho se incha a sua alma; ela não é reta nele; mas o justo, pela sua fé, viverá.'
        }
      },
      'Sofonias': {
        3: {
          17: 'O Senhor, teu Deus, está no meio de ti, poderoso para te salvar; ele se deleitará em ti com alegria; calar-se-á por seu amor, regozijar-se-á em ti com júbilo.'
        }
      },
      'Ageu': {
        2: {
          9: 'A glória desta última casa será maior do que a da primeira, diz o Senhor dos Exércitos, e neste lugar darei a paz, diz o Senhor dos Exércitos.'
        }
      },
      'Zacarias': {
        4: {
          6: 'Então, respondeu e me falou, dizendo: Esta é a palavra do Senhor a Zorobabel, dizendo: Não por força, nem por violência, mas pelo meu Espírito, diz o Senhor dos Exércitos.'
        }
      },
      'Malaquias': {
        3: {
          10: 'Trazei todos os dízimos à casa do tesouro, para que haja mantimento na minha casa, e depois fazei prova de mim, diz o Senhor dos Exércitos, se eu não vos abrir as janelas dos céus e não derramar sobre vós uma bênção tal, que dela vos advenha a maior abastança.'
        }
      },
      // ... existing code for New Testament books ...
      'Mateus': {
        5: {
          3: 'Bem-aventurados os pobres de espírito, porque deles é o Reino dos céus.',
          4: 'Bem-aventurados os que choram, porque eles serão consolados.',
          5: 'Bem-aventurados os mansos, porque eles herdarão a terra.',
          6: 'Bem-aventurados os que têm fome e sede de justiça, porque eles serão fartos.',
          7: 'Bem-aventurados os misericordiosos, porque eles alcançarão misericórdia.',
          8: 'Bem-aventurados os limpos de coração, porque eles verão a Deus.',
          9: 'Bem-aventurados os pacificadores, porque eles serão chamados filhos de Deus.',
          10: 'Bem-aventurados os que sofrem perseguição por causa da justiça, porque deles é o Reino dos céus.',
          11: 'Bem-aventurados sois vós quando vos injuriarem, e perseguirem, e, mentindo, disserem todo o mal contra vós, por minha causa.',
          12: 'Exultai e alegrai-vos, porque é grande o vosso galardão nos céus; porque assim perseguiram os profetas que foram antes de vós.'
        },
        6: {
          9: 'Portanto, vós orareis assim: Pai nosso, que estás nos céus, santificado seja o teu nome.',
          10: 'Venha o teu Reino. Seja feita a tua vontade, tanto na terra como no céu.',
          11: 'O pão nosso de cada dia nos dá hoje.',
          12: 'E perdoa-nos as nossas dívidas, assim como nós perdoamos aos nossos devedores.',
          13: 'E não nos indujas à tentação, mas livra-nos do mal; porque teu é o Reino, e o poder, e a glória, para sempre. Amém!'
        },
        28: {
          18: 'E, chegando-se Jesus, falou-lhes, dizendo: É-me dado todo o poder no céu e na terra.',
          19: 'Portanto, ide, ensinai todas as nações, batizando-as em nome do Pai, e do Filho, e do Espírito Santo;',
          20: 'ensinando-as a guardar todas as coisas que eu vos tenho mandado; e eis que eu estou convosco todos os dias, até à consumação dos séculos. Amém!'
        }
      },
      'João': {
        1: {
          1: 'No princípio, era o Verbo, e o Verbo estava com Deus, e o Verbo era Deus.',
          2: 'Ele estava no princípio com Deus.',
          3: 'Todas as coisas foram feitas por ele, e sem ele nada do que foi feito se fez.',
          4: 'Nele, estava a vida e a vida era a luz dos homens.',
          5: 'E a luz resplandece nas trevas, e as trevas não a compreenderam.',
          14: 'E o Verbo se fez carne e habitou entre nós, e vimos a sua glória, como a glória do Unigênito do Pai, cheio de graça e de verdade.'
        },
        3: {
          16: 'Porque Deus amou o mundo de tal maneira que deu o seu Filho unigênito, para que todo aquele que nele crê não pereça, mas tenha a vida eterna.',
          17: 'Porque Deus enviou o seu Filho ao mundo, não para que condenasse o mundo, mas para que o mundo fosse salvo por ele.',
          18: 'Quem crê nele não é condenado; mas quem não crê já está condenado, porquanto não crê no nome do unigênito Filho de Deus.'
        },
        14: {
          1: 'Não se turbe o vosso coração; credes em Deus, crede também em mim.',
          2: 'Na casa de meu Pai há muitas moradas; se não fosse assim, eu vo-lo teria dito, pois vou preparar-vos lugar.',
          3: 'E, se eu for e vos preparar lugar, virei outra vez e vos levarei para mim mesmo, para que, onde eu estiver, estejais vós também.',
          6: 'Disse-lhe Jesus: Eu sou o caminho, e a verdade, e a vida. Ninguém vem ao Pai senão por mim.'
        }
      },
      'Romanos': {
        8: {
          28: 'E sabemos que todas as coisas contribuem juntamente para o bem daqueles que amam a Deus, daqueles que são chamados por seu decreto.',
          31: 'Que diremos, pois, a estas coisas? Se Deus é por nós, quem será contra nós?',
          32: 'Aquele que nem mesmo a seu próprio Filho poupou, antes, o entregou por todos nós, como nos não dará também com ele todas as coisas?',
          35: 'Quem nos separará do amor de Cristo? A tribulação, ou a angústia, ou a perseguição, ou a fome, ou a nudez, ou o perigo, ou a espada?',
          37: 'Mas, em todas estas coisas, somos mais do que vencedores, por aquele que nos amou.',
          38: 'Porque estou certo de que nem a morte, nem a vida, nem os anjos, nem os principados, nem as potestades, nem o presente, nem o porvir,',
          39: 'nem a altura, nem a profundidade, nem alguma outra criatura nos poderá separar do amor de Deus, que está em Cristo Jesus, nosso Senhor.'
        }
      },
      '1 Coríntios': {
        13: {
          1: 'Ainda que eu falasse as línguas dos homens e dos anjos, e não tivesse caridade, seria como o metal que soa ou como o sino que tine.',
          2: 'E ainda que tivesse o dom de profecia, e conhecesse todos os mistérios e toda a ciência, e ainda que tivesse toda a fé, de maneira tal que transportasse os montes, e não tivesse caridade, nada seria.',
          3: 'E ainda que distribuísse toda a minha fazenda para sustento dos pobres, e ainda que entregasse o meu corpo para ser queimado, e não tivesse caridade, nada disso me aproveitaria.',
          4: 'A caridade é sofrida, é benigna; a caridade não é invejosa; a caridade não trata com leviandade, não se ensoberbece,',
          5: 'não se porta com indecência, não busca os seus interesses, não se irrita, não suspeita mal;',
          6: 'não folga com a injustiça, mas folga com a verdade;',
          7: 'tudo sofre, tudo crê, tudo espera, tudo suporta.',
          8: 'A caridade nunca falha; mas, havendo profecias, serão aniquiladas; havendo línguas, cessarão; havendo ciência, desaparecerá;',
          9: 'porque, em parte, conhecemos e, em parte, profetizamos.',
          10: 'Mas, quando vier o que é perfeito, então, o que o é em parte será aniquilado.',
          11: 'Quando eu era menino, falava como menino, sentia como menino, discorria como menino, mas, logo que cheguei a ser homem, acabei com as coisas de menino.',
          12: 'Porque, agora, vemos por espelho em enigma; mas, então, veremos face a face; agora, conheço em parte, mas, então, conhecerei como também sou conhecido.',
          13: 'Agora, pois, permanecem a fé, a esperança e a caridade, estas três; mas a maior destas é a caridade.'
        }
      },
      'Filipenses': {
        4: {
          4: 'Regozijai-vos, sempre, no Senhor; outra vez digo: regozijai-vos.',
          6: 'Não estejais inquietos por coisa alguma; antes, as vossas petições sejam em tudo conhecidas diante de Deus, pela oração e súplicas, com ação de graças.',
          7: 'E a paz de Deus, que excede todo o entendimento, guardará os vossos corações e os vossos sentimentos em Cristo Jesus.',
          8: 'Quanto ao mais, irmãos, tudo o que é verdadeiro, tudo o que é honesto, tudo o que é justo, tudo o que é puro, tudo o que é amável, tudo o que é de boa fama, se há alguma virtude, e se há algum louvor, nisso pensai.',
          13: 'Posso todas as coisas em Cristo que me fortalece.',
          19: 'O meu Deus, segundo as suas riquezas, suprirá todas as vossas necessidades em glória, por Cristo Jesus.'
        }
      },
      'Apocalipse': {
        21: {
          1: 'E vi um novo céu e uma nova terra. Porque já o primeiro céu e a primeira terra passaram, e o mar já não existe.',
          2: 'E eu, João, vi a Santa Cidade, a nova Jerusalém, que de Deus descia do céu, adereçada como uma esposa ataviada para o seu marido.',
          3: 'E ouvi uma grande voz do céu, que dizia: Eis aqui o tabernáculo de Deus com os homens, pois com eles habitará, e eles serão o seu povo, e o mesmo Deus estará com eles e será o seu Deus.',
          4: 'E Deus limpará de seus olhos toda lágrima, e não haverá mais morte, nem pranto, nem clamor, nem dor, porque já as primeiras coisas são passadas.',
          5: 'E o que estava assentado sobre o trono disse: Eis que faço novas todas as coisas. E disse-me: Escreve, porque estas palavras são verdadeiras e fiéis.',
          6: 'E disse-me mais: Está cumprido. Eu sou o Alfa e o Ômega, o Princípio e o Fim. A quem quer que tiver sede, de graça lhe darei da fonte da água da vida.',
          7: 'Quem vencer herdará todas as coisas, e eu serei seu Deus, e ele será meu filho.'
        }
      }
    };

    return bibleVerses[book]?.[chapter]?.[verse] || 
           `"${book} ${chapter}:${verse}" - A Palavra de Deus é viva e eficaz, mais cortante do que qualquer espada de dois gumes, e penetra até à divisão da alma, e do espírito, e das juntas e medulas, e é apta para discernir os pensamentos e intenções do coração. (Hebreus 4:12)`;
  };

  // Função para obter número de versículos por capítulo
  const getVerseCount = (book: string, chapter: number): number => {
    const verseCounts: { [key: string]: { [key: number]: number } } = {
      'Gênesis': {
        1: 31, 2: 25, 3: 24, 4: 26, 5: 32, 6: 22, 7: 24, 8: 22, 9: 29, 10: 32,
        11: 32, 12: 20, 13: 18, 14: 24, 15: 21, 16: 16, 17: 27, 18: 33, 19: 38, 20: 18,
        21: 34, 22: 24, 23: 20, 24: 31, 25: 34, 26: 35, 27: 46, 28: 22, 29: 35, 30: 43,
        31: 55, 32: 32, 33: 20, 34: 31, 35: 29, 36: 43, 37: 36, 38: 30, 39: 23, 40: 23,
        41: 57, 42: 38, 43: 34, 44: 34, 45: 28, 46: 34, 47: 31, 48: 22, 49: 33, 50: 26
      },
      'Êxodo': {
        1: 22, 2: 25, 3: 22, 4: 31, 5: 23, 6: 30, 7: 25, 8: 32, 9: 35, 10: 29,
        11: 10, 12: 51, 13: 22, 14: 31, 15: 27, 16: 36, 17: 16, 18: 27, 19: 25, 20: 26,
        21: 36, 22: 31, 23: 33, 24: 35, 25: 35, 26: 38, 27: 21, 28: 43, 29: 46, 30: 38,
        31: 18, 32: 35, 33: 23, 34: 35, 35: 35, 36: 38, 37: 29, 38: 31, 39: 43, 40: 38
      },
      'Levítico': {
        1: 17, 2: 16, 3: 17, 4: 35, 5: 19, 6: 30, 7: 38, 8: 36, 9: 24, 10: 20,
        11: 47, 12: 8, 13: 59, 14: 57, 15: 33, 16: 34, 17: 16, 18: 30, 19: 37, 20: 27,
        21: 24, 22: 33, 23: 34, 24: 23, 25: 55, 26: 46, 27: 34
      },
      'Números': {
        1: 54, 2: 34, 3: 51, 4: 49, 5: 31, 6: 27, 7: 89, 8: 26, 9: 23, 10: 36,
        11: 35, 12: 16, 13: 33, 14: 45, 15: 41, 16: 50, 17: 36, 18: 31, 19: 22, 20: 31,
        21: 35, 22: 41, 23: 30, 24: 25, 25: 18, 26: 65, 27: 23, 28: 31, 29: 40, 30: 16,
        31: 54, 32: 42, 33: 56, 34: 29, 35: 34, 36: 13
      },
      'Deuteronômio': {
        1: 46, 2: 37, 3: 29, 4: 49, 5: 33, 6: 25, 7: 26, 8: 22, 9: 29, 10: 22,
        11: 32, 12: 32, 13: 18, 14: 29, 15: 23, 16: 22, 17: 20, 18: 22, 19: 21, 20: 20,
        21: 23, 22: 30, 23: 25, 24: 22, 25: 19, 26: 19, 27: 26, 28: 68, 29: 29, 30: 20,
        31: 30, 32: 52, 33: 29, 34: 12
      },
      'Josué': {
        1: 36, 2: 24, 3: 17, 4: 24, 5: 15, 6: 27, 7: 26, 8: 35, 9: 27, 10: 43,
        11: 23, 12: 24, 13: 33, 14: 15, 15: 63, 16: 10, 17: 18, 18: 31, 19: 51, 20: 9,
        21: 45, 22: 34, 23: 16, 24: 33
      },
      'Juízes': {
        1: 36, 2: 23, 3: 31, 4: 24, 5: 31, 6: 40, 7: 25, 8: 35, 9: 57, 10: 18,
        11: 40, 12: 15, 13: 25, 14: 20, 15: 20, 16: 31, 17: 13, 18: 31, 19: 30, 20: 48, 21: 25
      },
      'Rute': {
        1: 22, 2: 23, 3: 18, 4: 22
      },
      '1 Samuel': {
        1: 28, 2: 36, 3: 21, 4: 22, 5: 12, 6: 21, 7: 17, 8: 22, 9: 27, 10: 27,
        11: 15, 12: 25, 13: 23, 14: 52, 15: 35, 16: 23, 17: 58, 18: 30, 19: 24, 20: 42,
        21: 15, 22: 23, 23: 29, 24: 22, 25: 44, 26: 25, 27: 12, 28: 25, 29: 11, 30: 31, 31: 13
      },
      '2 Samuel': {
        1: 27, 2: 32, 3: 39, 4: 12, 5: 25, 6: 23, 7: 29, 8: 18, 9: 13, 10: 19,
        11: 27, 12: 31, 13: 39, 14: 33, 15: 37, 16: 23, 17: 29, 18: 33, 19: 43, 20: 26,
        21: 22, 22: 51, 23: 39, 24: 25
      },
      '1 Reis': {
        1: 53, 2: 46, 3: 28, 4: 34, 5: 18, 6: 38, 7: 51, 8: 66, 9: 28, 10: 29,
        11: 43, 12: 33, 13: 34, 14: 31, 15: 34, 16: 34, 17: 24, 18: 46, 19: 21, 20: 43,
        21: 29, 22: 53
      },
      '2 Reis': {
        1: 18, 2: 25, 3: 27, 4: 44, 5: 27, 6: 33, 7: 20, 8: 29, 9: 37, 10: 36,
        11: 21, 12: 21, 13: 34, 14: 29, 15: 38, 16: 20, 17: 41, 18: 37, 19: 37, 20: 21,
        21: 26, 22: 20, 23: 37, 24: 20, 25: 30
      },
      '1 Crônicas': {
        1: 54, 2: 55, 3: 24, 4: 43, 5: 26, 6: 81, 7: 40, 8: 40, 9: 44, 10: 14,
        11: 47, 12: 40, 13: 14, 14: 17, 15: 29, 16: 43, 17: 27, 18: 17, 19: 19, 20: 8,
        21: 30, 22: 19, 23: 32, 24: 31, 25: 31, 26: 32, 27: 34, 28: 21, 29: 30
      },
      '2 Crônicas': {
        1: 17, 2: 18, 3: 17, 4: 22, 5: 14, 6: 42, 7: 22, 8: 18, 9: 31, 10: 19,
        11: 23, 12: 16, 13: 22, 14: 15, 15: 19, 16: 14, 17: 19, 18: 34, 19: 11, 20: 37,
        21: 20, 22: 12, 23: 21, 24: 27, 25: 28, 26: 23, 27: 9, 28: 27, 29: 36, 30: 27,
        31: 21, 32: 33, 33: 25, 34: 33, 35: 27, 36: 23
      },
      'Esdras': {
        1: 11, 2: 70, 3: 13, 4: 24, 5: 17, 6: 22, 7: 28, 8: 36, 9: 15, 10: 44
      },
      'Neemias': {
        1: 11, 2: 20, 3: 32, 4: 23, 5: 19, 6: 19, 7: 73, 8: 18, 9: 38, 10: 39,
        11: 36, 12: 47, 13: 31
      },
      'Ester': {
        1: 22, 2: 23, 3: 15, 4: 17, 5: 14, 6: 14, 7: 10, 8: 17, 9: 32, 10: 3
      },
      'Jó': {
        1: 22, 2: 13, 3: 26, 4: 21, 5: 27, 6: 30, 7: 21, 8: 22, 9: 35, 10: 22,
        11: 20, 12: 25, 13: 28, 14: 22, 15: 35, 16: 22, 17: 16, 18: 21, 19: 29, 20: 29,
        21: 34, 22: 30, 23: 17, 24: 25, 25: 6, 26: 14, 27: 23, 28: 28, 29: 25, 30: 31,
        31: 40, 32: 22, 33: 33, 34: 37, 35: 16, 36: 33, 37: 24, 38: 41, 39: 30, 40: 24,
        41: 34, 42: 17
      },
      'Salmos': {
        1: 6, 2: 12, 3: 8, 4: 8, 5: 12, 6: 10, 7: 17, 8: 9, 9: 20, 10: 18,
        11: 7, 12: 8, 13: 6, 14: 7, 15: 5, 16: 11, 17: 15, 18: 50, 19: 14, 20: 9,
        21: 13, 22: 31, 23: 6, 24: 10, 25: 22, 26: 12, 27: 14, 28: 28, 29: 25, 30: 31,
        31: 40, 32: 22, 33: 33, 34: 37, 35: 16, 36: 33, 37: 24, 38: 22, 39: 30, 40: 24,
        41: 34, 42: 17
      },
      'Provérbios': {
        1: 33, 2: 22, 3: 35, 4: 27, 5: 23, 6: 35, 7: 27, 8: 36, 9: 18, 10: 32,
        11: 31, 12: 28, 13: 25, 14: 35, 15: 33, 16: 33, 17: 28, 18: 24, 19: 29, 20: 30,
        21: 31, 22: 29, 23: 35, 24: 34, 25: 28, 26: 28, 27: 27, 28: 28, 29: 27, 30: 33, 31: 31
      },
      'Eclesiastes': {
        1: 18, 2: 26, 3: 22, 4: 16, 5: 20, 6: 12, 7: 29, 8: 17, 9: 18, 10: 20, 11: 10, 12: 14
      },
      'Cantares': {
        1: 17, 2: 17, 3: 11, 4: 16, 5: 16, 6: 13, 7: 13, 8: 14
      },
      'Isaías': {
        1: 31, 2: 22, 3: 26, 4: 6, 5: 30, 6: 13, 7: 25, 8: 22, 9: 21, 10: 34,
        11: 17, 12: 6, 13: 22, 14: 32, 15: 9, 16: 14, 17: 14, 18: 7, 19: 25, 20: 6,
        21: 17, 22: 25, 23: 18, 24: 23, 25: 12, 26: 21, 27: 13, 28: 29, 29: 24, 30: 33,
        31: 9, 32: 20, 33: 24, 34: 17, 35: 15, 36: 22, 37: 38, 38: 22, 39: 8, 40: 31,
        41: 29, 42: 25, 43: 28, 44: 28, 45: 25, 46: 13, 47: 7, 48: 47, 49: 39, 50: 46,
        51: 64, 52: 34
      },
      'Jeremias': {
        1: 19, 2: 37, 3: 25, 4: 31, 5: 31, 6: 30, 7: 34, 8: 22, 9: 26, 10: 25,
        11: 23, 12: 17, 13: 27, 14: 22, 15: 21, 16: 21, 17: 27, 18: 23, 19: 15, 20: 18,
        21: 14, 22: 30, 23: 40, 24: 10, 25: 38, 26: 24, 27: 22, 28: 17, 29: 32, 30: 24,
        31: 40, 32: 44, 33: 26, 34: 22, 35: 19, 36: 24, 37: 21, 38: 28, 39: 18, 40: 16,
        41: 18, 42: 22, 43: 13, 44: 30, 45: 5, 46: 14, 47: 7, 48: 47, 49: 39, 50: 46,
        51: 64, 52: 34
      },
      'Lamentações': {
        1: 22, 2: 22, 3: 66, 4: 22, 5: 22
      },
      'Ezequiel': {
        1: 28, 2: 10, 3: 27, 4: 17, 5: 17, 6: 14, 7: 27, 8: 18, 9: 11, 10: 22,
        11: 25, 12: 28, 13: 23, 14: 23, 15: 8, 16: 63, 17: 24, 18: 32, 19: 14, 20: 49,
        21: 32, 22: 31, 23: 49, 24: 27, 25: 17, 26: 49, 27: 28, 28: 23, 29: 29, 30: 26,
        31: 18, 32: 32, 33: 33, 34: 31, 35: 15, 36: 38, 37: 28, 38: 23, 39: 29, 40: 49,
        41: 26, 42: 20, 43: 27, 44: 31, 45: 25, 46: 24, 47: 23, 48: 35
      },
      'Daniel': {
        1: 21, 2: 49, 3: 30, 4: 37, 5: 31, 6: 28, 7: 28, 8: 27, 9: 27, 10: 21, 11: 45, 12: 13
      },
      'Oséias': {
        1: 11, 2: 23, 3: 5, 4: 19, 5: 15, 6: 11, 7: 16, 8: 14, 9: 17, 10: 15,
        11: 12, 12: 14, 13: 16, 14: 9
      },
      'Joel': {
        1: 20, 2: 32, 3: 21
      },
      'Amós': {
        1: 15, 2: 16, 3: 15, 4: 13, 5: 27, 6: 14, 7: 17, 8: 14, 9: 15
      },
      'Obadias': {
        1: 21
      },
      'Jonas': {
        1: 17, 2: 10, 3: 10, 4: 11
      },
      'Miquéias': {
        1: 16, 2: 13, 3: 12, 4: 13, 5: 15, 6: 16, 7: 20
      },
      'Naum': {
        1: 15, 2: 13, 3: 19
      },
      'Habacuque': {
        1: 17, 2: 20, 3: 19
      },
      'Sofonias': {
        1: 18, 2: 15, 3: 20
      },
      'Ageu': {
        1: 15, 2: 23
      },
      'Zacarias': {
        1: 21, 2: 13, 3: 17, 4: 14, 5: 11, 6: 17, 7: 14, 8: 23, 9: 17, 10: 12,
        11: 17, 12: 14, 13: 9, 14: 21
      },
      'Malaquias': {
        1: 14, 2: 17, 3: 18, 4: 6
      },
      'Mateus': {
        1: 25, 2: 23, 3: 17, 4: 25, 5: 48, 6: 34, 7: 29, 8: 34, 9: 38, 10: 42,
        11: 30, 12: 50, 13: 58, 14: 36, 15: 39, 16: 28, 17: 27, 18: 35, 19: 30, 20: 34,
        21: 46, 22: 46, 23: 39, 24: 51, 25: 46, 26: 75, 27: 66, 28: 20
      },
      'Marcos': {
        1: 45, 2: 28, 3: 35, 4: 41, 5: 43, 6: 56, 7: 37, 8: 38, 9: 50, 10: 52,
        11: 33, 12: 44, 13: 37, 14: 72, 15: 47, 16: 20
      },
      'Lucas': {
        1: 80, 2: 52, 3: 38, 4: 44, 5: 39, 6: 49, 7: 50, 8: 56, 9: 62, 10: 42,
        11: 54, 12: 59, 13: 35, 14: 35, 15: 32, 16: 31, 17: 37, 18: 43, 19: 48, 20: 31, 21: 25
      },
      'João': {
        1: 51, 2: 25, 3: 36, 4: 54, 5: 47, 6: 71, 7: 53, 8: 59, 9: 41, 10: 42,
        11: 57, 12: 50, 13: 38, 14: 31, 15: 27, 16: 33, 17: 26, 18: 40, 19: 42, 20: 31, 21: 25
      },
      'Atos': {
        1: 26, 2: 47, 3: 26, 4: 37, 5: 42, 6: 15, 7: 60, 8: 40, 9: 43, 10: 48,
        11: 30, 12: 25, 13: 52, 14: 28, 15: 41, 16: 31, 17: 34, 18: 28, 19: 41, 20: 38,
        21: 40, 22: 25, 23: 35, 24: 27, 25: 27, 26: 32, 27: 44, 28: 31
      },
      'Romanos': {
        1: 32, 2: 29, 3: 31, 4: 25, 5: 21, 6: 23, 7: 25, 8: 39, 9: 33, 10: 21,
        11: 36, 12: 21, 13: 14, 14: 23, 15: 27, 16: 21
      },
      '1 Coríntios': {
        1: 31, 2: 16, 3: 23, 4: 21, 5: 13, 6: 20, 7: 40, 8: 13, 9: 27, 10: 33,
        11: 34, 12: 31, 13: 14, 14: 40, 15: 27, 16: 21
      },
      '2 Coríntios': {
        1: 24, 2: 17, 3: 18, 4: 18, 5: 21, 6: 18, 7: 16, 8: 24, 9: 15, 10: 18,
        11: 33, 12: 21, 13: 14
      },
      'Gálatas': {
        1: 24, 2: 21, 3: 29, 4: 31, 5: 26, 6: 18
      },
      'Efésios': {
        1: 23, 2: 22, 3: 21, 4: 32, 5: 33, 6: 24
      },
      'Filipenses': {
        1: 30, 2: 30, 3: 21, 4: 23
      },
      'Colossenses': {
        1: 29, 2: 23, 3: 25, 4: 18
      },
      '1 Tessalonicenses': {
        1: 10, 2: 20, 3: 13, 4: 18, 5: 28
      },
      '2 Tessalonicenses': {
        1: 12, 2: 17, 3: 18
      },
      '1 Timóteo': {
        1: 20, 2: 15, 3: 16, 4: 16, 5: 25, 6: 21
      },
      '2 Timóteo': {
        1: 18, 2: 26, 3: 17, 4: 22
      },
      'Tito': {
        1: 16, 2: 15, 3: 15
      },
      'Filemom': {
        1: 25
      },
      'Hebreus': {
        1: 14, 2: 18, 3: 19, 4: 16, 5: 14, 6: 20, 7: 28, 8: 13, 9: 28, 10: 39,
        11: 40, 12: 29, 13: 25
      },
      'Tiago': {
        1: 27, 2: 26, 3: 18, 4: 17, 5: 20
      },
      '1 Pedro': {
        1: 25, 2: 25, 3: 22, 4: 19, 5: 14
      },
      '2 Pedro': {
        1: 21, 2: 22, 3: 18
      },
      '1 João': {
        1: 10, 2: 29, 3: 24, 4: 21, 5: 21
      },
      '2 João': {
        1: 13
      },
      '3 João': {
        1: 14
      },
      'Judas': {
        1: 25
      },
      'Apocalipse': {
        1: 20, 2: 29, 3: 22, 4: 11, 5: 14, 6: 17, 7: 17, 8: 13, 9: 21, 10: 11,
        11: 19, 12: 17, 13: 18, 14: 20, 15: 8, 16: 21, 17: 18, 18: 24, 19: 21, 20: 15,
        21: 27, 22: 21
      }
    };

    return verseCounts[book]?.[chapter] || 25; // Default 25 versículos
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-600">
      {/* Header Responsivo */}
      <header className="bg-white/98 backdrop-blur-lg shadow-2xl border-b-4 border-gradient-to-r from-indigo-600 to-purple-600">
        <div className="px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            {/* Logo e Título */}
            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 lg:space-x-8 w-full lg:w-auto">
              <img
                src="https://static.readdy.ai/image/8fbfc975813ef59021c9f16b313e86f9/f5810bc36c96b4c4a03e9a9aa341e507.png"
                alt="Assembleia de Deus - Missão Europa"
                className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 object-contain shadow-2xl rounded-2xl border-4 border-indigo-200 hover:scale-105 transition-transform duration-300"
              />
              <div className="text-center">
                <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
                  <span style={{ fontFamily: '"Agnes", serif' }}>IGREJA AD MISSÃO EUROPA CHURCH</span>
                </h1>
                <p className="text-sm sm:text-base lg:text-xl text-gray-700 font-semibold">Missão Europa Church Portugal • 66 Livros • 31.102 Versículos</p>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  <span className="bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-2 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                    📜 Antigo Testamento: 39 livros
                  </span>
                  <span className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 px-2 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                    ✝️ Novo Testamento: 27 livros
                  </span>
                </div>
              </div>
            </div>

            {/* Navegação Responsiva */}
            <nav className="w-full lg:w-auto">
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:flex lg:items-center gap-2 lg:gap-3 xl:gap-4">
                <button
                  onClick={() => safeNavigate('/home')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line text-lg"></i>
                  <span>Início</span>
                </button>

                <button
                  onClick={() => safeNavigate('/coimep')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-building-line text-lg"></i>
                  <span>COIMEP</span>
                </button>

                <button
                  onClick={() => safeNavigate('/bible')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-indigo-600 bg-indigo-100 rounded-xl hover:bg-indigo-200 transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-line text-lg"></i>
                  <span>Bíblia</span>
                </button>

                <button
                  onClick={() => safeNavigate('/characters')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-user-star-line text-lg"></i>
                  <span>Personagens</span>
                </button>

                <button
                  onClick={() => safeNavigate('/devotionals')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-heart-line text-lg"></i>
                  <span>Devocionais</span>
                </button>

                <button
                  onClick={() => safeNavigate('/studies')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-graduation-cap-line text-lg"></i>
                  <span>Estudos</span>
                </button>

                <button
                  onClick={() => safeNavigate('/radio')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-radio-line text-lg"></i>
                  <span>Rádio</span>
                </button>

                <button
                  onClick={() => safeNavigate('/kids')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-bear-smile-line text-lg"></i>
                  <span>Crianças</span>
                </button>

                <button
                  onClick={() => safeNavigate('/prayer-requests')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-hands-line text-lg"></i>
                  <span>Oração</span>
                </button>

                <button
                  onClick={() => safeNavigate('/filiais')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-building-2-line text-lg"></i>
                  <span>Filiais</span>
                </button>

                <button
                  onClick={() => safeNavigate('/location')}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-map-pin-line text-lg"></i>
                  <span>Localização</span>
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center space-x-1 lg:space-x-2 px-2 py-2 lg:px-4 lg:py-2 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 text-xs sm:text-sm lg:text-base font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-logout-box-line text-lg"></i>
                  <span>Sair</span>
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Banner Responsivo */}
      <div className="relative h-48 sm:h-64 lg:h-96 bg-gradient-to-r from-indigo-800 via-purple-800 to-pink-800 overflow-hidden">
        <div className="absolute inset-0 bg-black/40"></div>
        <img
          src="https://public.readdy.ai/ai/img_res/e1ba53eac66e750c60c3087bd09d00e8.jpg"
          alt="Bíblia Sagrada Completa"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="text-center text-white">
            <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold mb-2 lg:mb-6 drop-shadow-2xl">📖 Bíblia Sagrada Completa</h2>
            <p className="text-sm sm:text-base lg:text-xl opacity-95 mb-2 lg:mb-4 drop-shadow-lg">66 Livros • 1.189 Capítulos • 31.102 Versículos</p>
            <p className="text-xs sm:text-sm lg:text-lg opacity-85 mb-2 lg:mb-3">Antigo Testamento (39 livros) • Novo Testamento (27 livros)</p>
            <p className="text-xs sm:text-sm lg:text-lg opacity-75 mt-2 lg:mt-4 bg-black/30 px-3 py-1 lg:px-6 lg:py-2 rounded-full inline-block">✨ "A Palavra de Deus é viva e eficaz" - Hebreus 4:12</p>
          </div>
        </div>
      </div>

      {/* Main Content Responsivo */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-12">
          {/* Book Selection Responsivo */}
          <div className="xl:col-span-1">
            <div className="bg-white/98 backdrop-blur-lg rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 border border-white/30">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4 lg:mb-8">
                📚 Selecionar Livro
              </h3>

              {/* Testament Switcher Responsivo */}
              <div className="flex bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 rounded-2xl p-1 lg:p-2 mb-4 lg:mb-8 shadow-inner">
                <button
                  onClick={() => setTestament('old')}
                  className={`flex-1 py-2 lg:py-4 px-2 lg:px-6 rounded-xl text-xs sm:text-sm lg:text-sm font-bold transition-all duration-300 cursor-pointer ${
                    testament === 'old' ? 'bg-white text-indigo-700 shadow-xl transform scale-105' : 'text-gray-600 hover:text-indigo-600'
                  }`}
                >
                  📜 Antigo (39)
                </button>
                <button
                  onClick={() => setTestament('new')}
                  className={`flex-1 py-2 lg:py-4 px-2 lg:px-6 rounded-xl text-xs sm:text-sm lg:text-sm font-bold transition-all duration-300 cursor-pointer ${
                    testament === 'new' ? 'bg-white text-purple-700 shadow-xl transform scale-105' : 'text-gray-600 hover:text-purple-600'
                  }`}
                >
                  ✝️ Novo (27)
                </button>
              </div>

              {/* Book List Responsivo */}
              <div className="space-y-2 lg:space-y-3 max-h-64 sm:max-h-80 lg:max-h-[500px] overflow-y-auto custom-scrollbar">
                {currentBooks.map((book, index) => (
                  <button
                    key={book.name}
                    onClick={() => {
                      setSelectedBook(book.name);
                      setSelectedChapter(1);
                    }}
                    className={`w-full text-left px-3 lg:px-6 py-2 lg:py-4 rounded-2xl transition-all duration-300 cursor-pointer transform hover:scale-102 ${
                      selectedBook === book.name
                        ? 'bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 text-indigo-800 border-l-4 lg:border-l-6 border-indigo-600 shadow-xl'
                        : 'hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 text-gray-700 hover:shadow-lg'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-sm lg:text-lg">
                        {index + 1}. {book.name}
                      </span>
                      <span className="text-xs lg:text-sm text-gray-500 bg-gray-100 px-2 lg:px-3 py-1 rounded-full font-medium">{book.chapters} cap.</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Testament Summary Responsivo */}
              <div className="mt-4 lg:mt-8 p-3 lg:p-6 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-2xl border-2 border-indigo-200 shadow-lg">
                <h4 className="font-bold text-indigo-700 mb-2 lg:mb-3 text-sm lg:text-lg">
                  {testament === 'old' ? '📜 Antigo Testamento' : '✝️ Novo Testamento'}
                </h4>
                <p className="text-gray-700 font-medium text-xs lg:text-base">
                  {testament === 'old' 
                    ? 'Lei, História, Poesia e Profetas • Gênesis até Malaquias'
                    : 'Evangelhos, História, Cartas e Profecia • Mateus até Apocalipse'
                  }
                </p>
                <p className="text-xs lg:text-sm text-gray-600 mt-1 lg:mt-2 font-medium">
                  {testament === 'old' ? '39 livros • 929 capítulos' : '27 livros • 260 capítulos'}
                </p>
              </div>
            </div>

            {/* Characters Button Responsivo */}
            {selectedBook && (
              <div className="mt-4 lg:mt-8">
                <button
                  onClick={() => handleViewCharacter(selectedBook)}
                  className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white py-3 lg:py-5 rounded-2xl font-bold text-sm lg:text-lg hover:from-purple-700 hover:via-pink-700 hover:to-red-700 transition-all duration-300 shadow-2xl flex items-center justify-center space-x-2 lg:space-x-3 cursor-pointer transform hover:scale-105"
                >
                  <i className="ri-user-star-line text-lg lg:text-2xl"></i>
                  <span>Ver Personagens de {selectedBook}</span>
                </button>
              </div>
            )}
          </div>

          {/* Bible Content Responsivo */}
          <div className="xl:col-span-2">
            <div className="bg-white/98 backdrop-blur-lg rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-10 border border-white/30">
              {selectedBook ? (
                <>
                  {/* Header with selected book and chapter selector Responsivo */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 lg:mb-10 space-y-4 sm:space-y-0">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                      📖 {selectedBook}
                    </h2>
                    <div className="flex items-center space-x-3 lg:space-x-6">
                      <span className="text-sm sm:text-lg lg:text-xl text-gray-700 font-bold">Capítulo:</span>
                      <select
                        value={selectedChapter}
                        onChange={(e) => setSelectedChapter(Number(e.target.value))}
                        className="px-3 py-2 lg:px-6 lg:py-4 border-2 border-indigo-300 rounded-2xl focus:ring-4 focus:ring-indigo-500 focus:border-transparent pr-8 lg:pr-10 bg-gradient-to-r from-indigo-50 to-purple-50 text-sm sm:text-lg lg:text-xl font-bold shadow-lg"
                      >
                        {Array.from({ length: maxChapters }, (_, i) => i + 1).map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Chapter Title Responsivo */}
                  <div className="prose max-w-none">
                    <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border-l-4 lg:border-l-6 border-indigo-600 p-4 lg:p-8 mb-6 lg:mb-10 rounded-r-2xl shadow-lg">
                      <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-indigo-800 via-purple-800 to-pink-800 bg-clip-text text-transparent mb-2 lg:mb-4">
                        📜 {selectedBook} {selectedChapter}
                      </h3>
                      <p className="text-sm sm:text-lg lg:text-xl text-gray-700 font-medium">
                        {selectedBookData && `Total de ${selectedBookData.chapters} capítulos neste livro • ${getVerseCount(selectedBook, selectedChapter)} versículos neste capítulo`}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 lg:gap-6 mt-2 lg:mt-4 text-gray-600">
                        <span className="flex items-center space-x-1 lg:space-x-2 font-medium text-xs sm:text-sm lg:text-base">
                          <i className="ri-book-line text-sm lg:text-lg"></i>
                          <span>Livro {currentBooks.findIndex(b => b.name === selectedBook) + 1} de {currentBooks.length}</span>
                        </span>
                        <span className="flex items-center space-x-1 lg:space-x-2 font-medium text-xs sm:text-sm lg:text-base">
                          <i className="ri-file-text-line text-sm lg:text-lg"></i>
                          <span>Capítulo {selectedChapter} de {maxChapters}</span>
                        </span>
                      </div>
                    </div>

                    {/* Verses Responsivo */}
                    <div className="space-y-4 lg:space-y-8">
                      {Array.from({ length: Math.min(getVerseCount(selectedBook, selectedChapter), 10) }, (_, i) => (
                        <div
                          key={i + 1}
                          className="flex space-x-3 lg:space-x-6 p-4 lg:p-8 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 hover:to-pink-50 rounded-2xl transition-all duration-300 border border-transparent hover:border-indigo-200 shadow-sm hover:shadow-xl transform hover:scale-102"
                        >
                          {/* Verse number badge Responsivo */}
                          <span className="flex-shrink-0 w-8 h-8 lg:w-14 lg:h-14 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-sm lg:text-xl font-bold shadow-2xl">
                            {i + 1}
                          </span>

                          {/* Verse text and actions Responsivo */}
                          <div className="flex-1">
                            <p className="text-gray-800 leading-relaxed text-sm sm:text-base lg:text-xl mb-3 lg:mb-6 font-medium">
                              {getBibleVerse(selectedBook, selectedChapter, i + 1)}
                            </p>

                            <div className="flex flex-wrap gap-3 lg:gap-8">
                              <button
                                onClick={() => handleViewStudy(selectedBook, selectedChapter, i + 1)}
                                className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1 lg:space-x-2 cursor-pointer transition-all duration-200 font-bold text-xs sm:text-sm lg:text-lg hover:scale-105"
                              >
                                <i className="ri-book-open-line text-sm lg:text-xl"></i>
                                <span>Ver estudo bíblico</span>
                              </button>

                              <button
                                onClick={() => handleViewCharacter(selectedBook)}
                                className="text-purple-600 hover:text-purple-800 flex items-center space-x-1 lg:space-x-2 cursor-pointer transition-all duration-200 font-bold text-xs sm:text-sm lg:text-lg hover:scale-105"
                              >
                                <i className="ri-user-star-line text-sm lg:text-xl"></i>
                                <span>Personagens</span>
                              </button>

                              <span className="text-gray-400 flex items-center space-x-1 lg:space-x-2 font-medium text-xs sm:text-sm lg:text-base">
                                <i className="ri-bookmark-line"></i>
                                <span>{selectedBook} {selectedChapter}:{i + 1}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Chapter Navigation Responsivo */}
                    <div className="flex flex-col sm:flex-row justify-between items-center mt-8 lg:mt-16 p-4 lg:p-8 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-2xl border-2 border-indigo-200 shadow-xl space-y-4 sm:space-y-0">
                      <button
                        onClick={() => selectedChapter > 1 && setSelectedChapter(selectedChapter - 1)}
                        disabled={selectedChapter <= 1}
                        className={`flex items-center space-x-2 lg:space-x-3 px-4 py-2 lg:px-8 lg:py-4 rounded-2xl font-bold transition-all duration-300 cursor-pointer text-sm lg:text-lg ${
                          selectedChapter > 1
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-2xl transform hover:scale-105'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <i className="ri-arrow-left-line text-sm lg:text-xl"></i>
                        <span>Anterior</span>
                      </button>

                      <div className="text-center">
                        <p className="text-lg lg:text-2xl font-bold text-gray-800">
                          {selectedBook} {selectedChapter}
                        </p>
                        <p className="text-sm lg:text-xl text-gray-600 font-medium">
                          {getVerseCount(selectedBook, selectedChapter)} versículos
                        </p>
                      </div>

                      <button
                        onClick={() => selectedChapter < maxChapters && setSelectedChapter(selectedChapter + 1)}
                        disabled={selectedChapter >= maxChapters}
                        className={`flex items-center space-x-2 lg:space-x-3 px-4 py-2 lg:px-8 lg:py-4 rounded-2xl font-bold transition-all duration-300 cursor-pointer text-sm lg:text-lg ${
                          selectedChapter < maxChapters
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-2xl transform hover:scale-105'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <span>Próximo</span>
                        <i className="ri-arrow-right-line text-sm lg:text-xl"></i>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // Empty state when no book selected Responsivo
                <div className="text-center py-10 lg:py-20">
                  <i className="ri-book-open-line text-6xl lg:text-9xl text-gray-300 mb-4 lg:mb-8"></i>
                  <h3 className="text-xl lg:text-3xl font-bold text-gray-700 mb-3 lg:mb-6">📖 Selecione um Livro da Bíblia</h3>
                  <p className="text-sm lg:text-xl text-gray-600 mb-4 lg:mb-8">Escolha um dos 66 livros da Bíblia Sagrada para começar a leitura</p>
                  
                  {/* Bible Statistics Responsivo */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-8 mt-6 lg:mt-12">
                    <div className="bg-indigo-50 p-4 lg:p-8 rounded-2xl border-2 border-indigo-200 shadow-xl">
                      <h4 className="font-bold text-indigo-700 mb-2 lg:mb-4 text-lg lg:text-2xl">📜 Antigo Testamento</h4>
                      <div className="space-y-2 lg:space-y-3 text-gray-700 text-sm lg:text-base">
                        <p className="font-bold"><strong>39 livros</strong> • 929 capítulos</p>
                        <p><strong>Lei:</strong> Gênesis - Deuteronômio</p>
                        <p><strong>História:</strong> Josué - Ester</p>
                        <p><strong>Poesia:</strong> Jó - Cântico dos Cânticos</p>
                        <p><strong>Profetas:</strong> Isaías - Malaquias</p>
                      </div>
                    </div>
                    <div className="bg-purple-50 p-4 lg:p-8 rounded-2xl border-2 border-purple-200 shadow-xl">
                      <h4 className="font-bold text-purple-700 mb-2 lg:mb-4 text-lg lg:text-2xl">✝️ Novo Testamento</h4>
                      <div className="space-y-2 lg:space-y-3 text-gray-700 text-sm lg:text-base">
                        <p className="font-bold"><strong>27 livros</strong> • 260 capítulos</p>
                        <p><strong>Evangelhos:</strong> Mateus - João</p>
                        <p><strong>História:</strong> Atos dos Apóstolos</p>
                        <p><strong>Cartas:</strong> Romanos - Judas</p>
                        <p><strong>Profecia:</strong> Apocalipse</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Access Responsivo */}
                  <div className="mt-6 lg:mt-12 p-4 lg:p-8 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border-2 border-yellow-200 shadow-xl">
                    <h4 className="font-bold text-orange-700 mb-3 lg:mb-6 text-lg lg:text-2xl">⭐ Acesso Rápido</h4>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
                      <button
                        onClick={() => {
                          setSelectedBook('Gênesis');
                          setSelectedChapter(1);
                          setTestament('old');
                        }}
                        className="bg-white p-3 lg:p-6 rounded-2xl hover:bg-orange-50 transition-all duration-300 cursor-pointer border-2 border-orange-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <strong className="text-sm lg:text-lg">Gênesis 1</strong><br/>
                        <span className="text-gray-600 text-xs lg:text-base">Criação</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBook('Salmos');
                          setSelectedChapter(23);
                          setTestament('old');
                        }}
                        className="bg-white p-3 lg:p-6 rounded-2xl hover:bg-orange-50 transition-all duration-300 cursor-pointer border-2 border-orange-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <strong className="text-sm lg:text-lg">Salmo 23</strong><br/>
                        <span className="text-gray-600 text-xs lg:text-base">O Bom Pastor</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBook('João');
                          setSelectedChapter(3);
                          setTestament('new');
                        }}
                        className="bg-white p-3 lg:p-6 rounded-2xl hover:bg-orange-50 transition-all duration-300 cursor-pointer border-2 border-orange-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <strong className="text-sm lg:text-lg">João 3</strong><br/>
                        <span className="text-gray-600 text-xs lg:text-base">Novo Nascimento</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBook('1 Coríntios');
                          setSelectedChapter(13);
                          setTestament('new');
                        }}
                        className="bg-white p-3 lg:p-6 rounded-2xl hover:bg-orange-50 transition-all duration-300 cursor-pointer border-2 border-orange-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <strong className="text-sm lg:text-lg">1 Coríntios 13</strong><br/>
                        <span className="text-gray-600 text-xs lg:text-base">O Amor</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #6366f1, #8b5cf6);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #4f46e5, #7c3aed);
        }
      `}</style>
    </div>
  );
}
